/** Canonical opening hours for Yawas Kebab (Europe/Zurich). */

export type OpeningHoursRow = { day: string; hours: string };

/** Multiline value stored in site_settings.restaurant_hours.
 *  "Label: Zeit" per line; a line without ": " continues the previous day's hours. */
export const DEFAULT_RESTAURANT_HOURS = [
  'Montag – Samstag: 11:00 Uhr – 13:30 Uhr',
  '17:00 Uhr – 19:30 Uhr',
  'Sonntag: geschlossen',
].join('\n');

export const DEFAULT_OPENING_HOURS: OpeningHoursRow[] = [
  { day: 'Montag – Samstag', hours: '11:00 Uhr – 13:30 Uhr' },
  { day: '', hours: '17:00 Uhr – 19:30 Uhr' },
  { day: 'Sonntag', hours: 'geschlossen' },
];

/** Short one-line summary (menu header fallback). */
export const DEFAULT_RESTAURANT_HOURS_SHORT =
  'Mo–Sa: 11:00–13:30 & 17:00–19:30 · So: geschlossen';

export function parseOpeningHours(raw: string | null | undefined): OpeningHoursRow[] {
  const text = (raw ?? '').trim();
  if (!text) return DEFAULT_OPENING_HOURS;

  const rows = text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const idx = line.indexOf(': ');
      if (idx === -1) return { day: '', hours: line };
      return {
        day: line.slice(0, idx).trim(),
        hours: line.slice(idx + 2).trim(),
      };
    });

  return rows.length > 0 ? rows : DEFAULT_OPENING_HOURS;
}

function zurichParts(date: Date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Zurich',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const weekday = get('weekday'); // Mon, Tue, ...
  const hour = Number(get('hour'));
  const minute = Number(get('minute'));
  return { weekday, minutes: hour * 60 + minute };
}

/** Mo–Sa: 11:00–13:30 and 17:00–19:30 · Sunday closed (Zurich time). */
export function isRestaurantOpenNow(date = new Date()): boolean {
  const { weekday, minutes } = zurichParts(date);
  if (weekday === 'Sun') return false;
  if (weekday === 'Sat' || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].includes(weekday)) {
    const lunch = minutes >= 11 * 60 && minutes < 13 * 60 + 30;
    const dinner = minutes >= 17 * 60 && minutes < 19 * 60 + 30;
    return lunch || dinner;
  }
  return false;
}
